// @file vuexstore for padlet authors/commentors information
import { JsonAPIResource, User } from '@padlet/arvo'
import { captureFetchException } from 'bits/error_tracker'
import { vSet } from 'bits/vue'
import { sortBy } from 'lodash-es'
import PadletApi from 'surface/padlet_api'
import type { Id } from 'types'
import { Module } from 'vuex'
import { RootState } from 'vuexstore/surface/types'

export interface UserContributorState {
  userById: Record<Id, User>
  userIdsBeingFetched: Id[]
}

const defaultState = (): UserContributorState => ({
  userById: {},
  userIdsBeingFetched: [],
})

const UserContributorModule: Module<UserContributorState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    getUserById:
      (state): Function =>
      (id: Id): User | null =>
        state.userById[id],
    isUserIdBeingFetched:
      (state): Function =>
      (id: Id): boolean =>
        state.userIdsBeingFetched.includes(id),
    postAuthors: (state, _getters, _rootState, rootGetters): User[] =>
      Object.values(state.userById).filter((u) => rootGetters['post/postAuthorIds'].includes(u.id)),
    registeredPostAuthors: (state, getters, rootState, rootGetters): User[] =>
      sortBy(
        getters.postAuthors.filter((u) => u.registered),
        (u: User): string | undefined => {
          return rootGetters.builder.id === u.id ? '' : u.short_name
        },
      ),
    anonymousPostAuthors: (_, getters): User[] => getters.postAuthors.filter((u) => !u.registered),
    totalPostAuthors: (_state, _getters, _rootState, rootGetters): number => rootGetters['post/postAuthorIds'].length,
  },
  mutations: {
    ADD_USER(state, newUser): void {
      vSet(state.userById, newUser.id, newUser)
    },
    ADD_BEING_FETCHED_USER_ID(state, userId): void {
      state.userIdsBeingFetched.push(userId)
    },
    REMOVE_BEING_FETCHED_USER_ID(state, userId): void {
      state.userIdsBeingFetched = state.userIdsBeingFetched.filter((id) => id !== userId)
    },
  },
  actions: {
    async fetchUser({ commit, getters }, userId: Id): Promise<void> {
      if (getters.getUserById(userId) || getters.isUserIdBeingFetched(userId)) {
        return
      }

      commit('ADD_BEING_FETCHED_USER_ID', userId)
      try {
        const fetchedUserData = await PadletApi.UserContributor.read(userId)
        commit('ADD_USER', (fetchedUserData.data as JsonAPIResource<User>).attributes)
        commit('REMOVE_BEING_FETCHED_USER_ID', userId)
      } catch (e) {
        captureFetchException(e, { source: 'fetchUserContributorInfo' })
      }
    },
    // For the contributors panel (Currently it only shows post authors)
    async fetchPostAuthors({ dispatch, rootGetters }): Promise<void> {
      try {
        rootGetters['post/postAuthorIds'].forEach((userId) => {
          dispatch('fetchUser', userId)
        })
      } catch (e) {
        captureFetchException(e, { source: 'fetchPostAuthors' })
      }
    },
    async fetchAndWaitForAuthors({ dispatch, rootGetters }): Promise<void> {
      try {
        await Promise.all(
          rootGetters['post/postAuthorIds'].map(
            async (userId: Id): Promise<void> => await dispatch('fetchUser', userId),
          ),
        )
      } catch (e) {
        captureFetchException(e, { source: 'fetchAndWaitForAuthors' })
      }
    },
  },
}

export default UserContributorModule
